import RwAdvisorService from '@advisor/rwSdk/AdvisorService';
import RwPlatformSettingsService from '@advisor/rwSdk/PlatformSettingsService';
import SegmentSettingsModel from '@advisor/models/store/settings/SegmentSettings';
import SegmentModel from '@advisor/models/store/settings/Segment';
import GoalModel from '@advisor/models/store/settings/onboarding/Goal';
import AccountOpeningModel from '@advisor/models/store/settings/onboarding/AccountOpening';
import exceptionHandler from '@advisor/util/exceptionHandler';
import {addRiaComponentTo} from '@advisor/util/store/paramInjects';

export const segmentSettingState = () => new SegmentSettingsModel();

export const getters = {
  segments: (state) => state.segments.map((segment) => SegmentModel.make(segment)),
  featureListMap: (state) => {
    const featureMap = new Map();

    state.currentSegment.featureList.forEach((objFeature) => {
      featureMap.set(objFeature.featureConstant, objFeature);
    });

    return featureMap;
  },
  segmentListMap: (state) => {
    const segmentMap = new Map();

    state.segments.forEach((objSegment) => {
      segmentMap.set(objSegment.segmentId, objSegment);
    });

    return segmentMap;
  },
};

export const actions = {
  async getClientSegments({commit, rootState}) {
    try {
      const {riaId} = rootState;
      const fullPayload = addRiaComponentTo({riaId}, rootState.settings.default);

      const data = await RwAdvisorService.getSegments(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_SEGMENT_SETTINGS', data);

      return Promise.resolve(data);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.getClientSegments', error);

      return Promise.reject(error);
    }
  },
  setCurrentSegment({commit}, payload) {
    commit('SET_CURRENT_SEGMENT', payload);
  },
  async getSegmentGoalList({commit, rootState}, payload) {
    try {
      const {riaId} = rootState;

      const fullPayload = addRiaComponentTo({...payload, riaId}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.getSegmentGoals(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_GOAL_LIST', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.getSegmentGoalList', error);

      return Promise.reject(error);
    }
  },
  /**
   *
   * @typedef {Object} RepCode
   * @property {Number} repId
   * @property {String} repCode
   *
   *
   * @typedef {Object} CustodianType
   * @property {String} custodianCode
   * @property {String} custodianLogo
   * @property {String} custodianName
   * @property {Array<RepCode>} custodianRepCode
   * @property {Number} riaId
   * @property {Number} custodianId
   *
   *
   *
   * @param {Context} context
   * @param {Object} payload
   */
  async getOnboardingData({commit, dispatch, rootState}, payload) {
    const {riaId} = rootState;

    try {
      const sdkPayload = {...payload, riaId};
      const fullPayload = addRiaComponentTo(sdkPayload, rootState.settings.default);

      const data = await RwAdvisorService.getOnboardingData(fullPayload).catch((error) => {
        throw error;
      });

      if (data) {
        const clientOnboarding = data;

        /**
         * @type {Array<CustodianType>}
         */
        const custodians = await dispatch('custodian/getCustodianTypes', null, {
          root: true,
        });

        clientOnboarding.custodian = custodians.find((custodian) => custodian.custodianId === clientOnboarding.custodianId);

        clientOnboarding.repCode = clientOnboarding.custodian?.custodianRepCode.find(
          (repCode) => repCode.repId === clientOnboarding.repId
        );

        commit('SET_ACCOUNT_OPENING', clientOnboarding);
      }

      return Promise.resolve(data);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.getOnboardingData', error);

      return Promise.reject(error);
    }
  },
  async createUpdateOnboardingData({rootState}, payload) {
    try {
      const {riaId} = rootState;

      const {segmentId, clientOnBoardingRequest} = payload;
      const sdkRequest = {
        riaId,
        segmentId,
        payload: clientOnBoardingRequest,
      };
      const fullPayload = addRiaComponentTo(sdkRequest, rootState.settings.default);

      const sdkResponse = await RwAdvisorService.createUpdateOnboardingData(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.createUpdateOnboardingData', error);

      return Promise.reject(error);
    }
  },
  async getGoalThumbnailList({commit, rootState}) {
    const {riaId} = rootState;

    try {
      const fullPayload = addRiaComponentTo({riaId}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.getSegmentThumbnails(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_GOAL_THUMBNAILS', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.getGoalThumbnailList', error);

      return Promise.reject(error);
    }
  },
  async updateSegment({rootState, commit}, payload) {
    try {
      const {riaId} = rootState;

      const {segmentId, segmentRequest} = payload;
      const fullPayload = addRiaComponentTo({riaId, segmentId, payload: segmentRequest}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.createUpdateSegmentGoal(fullPayload).catch((error) => {
        throw error;
      });

      commit('UPDATE_SEGMENT_NAME', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.updateSegment', error);

      return Promise.reject(error);
    }
  },
  async deleteSegment({rootState}, payload) {
    try {
      const {riaId} = rootState;

      const fullPayload = addRiaComponentTo({...payload, riaId}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.deleteSegment(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.deleteSegment', error);

      return Promise.reject(error);
    }
  },
  async deleteGoal({rootState}, payload) {
    try {
      const {riaId} = rootState;

      const fullPayload = addRiaComponentTo({...payload, riaId}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.deleteSegmentGoal(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.deleteGoal', error);

      return Promise.reject(error);
    }
  },
  async getClientStats({rootState}, payload) {
    try {
      const {riaId} = rootState;

      const fullPayload = addRiaComponentTo({...payload, riaId}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.getSegmentStats(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.getClientStats', error);

      return Promise.reject(error);
    }
  },
  async getFeatureList({commit, rootState}, payload) {
    try {
      const {riaId} = rootState;

      const fullPayload = addRiaComponentTo({...payload, riaId}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.getSegmentFeatures(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_CURRENT_SEGMENT_FEATURES', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.getFeatureList', error);

      return Promise.reject(error);
    }
  },
  async updateFeatureStatus({commit, rootState}, payload) {
    try {
      const {riaId} = rootState;
      const {segmentId, featureConstant, featureStatus} = payload;

      const sdkRequest = {
        riaId,
        segmentId,
        payload: {featureConstant, enableFeature: featureStatus},
      };
      const fullPayload = addRiaComponentTo(sdkRequest, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.updateSegmentFeatureStatus(fullPayload).catch((error) => {
        throw error;
      });

      commit('UPDATE_FEATURE_STATUS', sdkRequest.payload);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.updateFeatureStatus', error);

      return Promise.reject(error);
    }
  },

  async updateSegmentFeatureValue({rootState}, payload) {
    try {
      const {riaId} = rootState;

      const {segmentId, featureConstant, featureValue} = payload;

      const sdkRequest = {
        riaId,
        segmentId,
        segmentFeatureValueRequest: {featureConstant, featureValue},
      };
      const fullPayload = addRiaComponentTo(sdkRequest, rootState.settings.default);
      const sdkResponse = await RwPlatformSettingsService.updateSegmentFeatureValue(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.updateSegmentFeatureValue', error);

      return Promise.reject(error);
    }
  },

  async getSegment({commit, rootState}, payload) {
    try {
      const {riaId} = rootState;

      const fullPayload = addRiaComponentTo({...payload, riaId}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.getSegment(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_CURRENT_SEGMENT', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.getSegment', error);

      return Promise.reject(error);
    }
  },
  async fetchSegmentDetails({dispatch}, {segmentId}) {
    try {
      const payload = {segmentId, limit: 10, offset: 0};

      await dispatch('getSegment', payload);
      await Promise.all([
        dispatch('getOnboardingData', payload),
        dispatch('getSegmentGoalList', payload),
        dispatch('getFeatureList', payload),
      ]);

      return true;
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.fetchSegmentDetails', error);

      return Promise.reject(error);
    }
  },
  async addUpdateGoal({dispatch, rootState}, {segmentId, goalRequest}) {
    try {
      const {riaId} = rootState;

      const payload = goalRequest;
      const fullPayload = addRiaComponentTo({riaId, segmentId, payload}, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.createUpdateSegmentGoal(fullPayload).catch((error) => {
        throw error;
      });

      await dispatch('getSegmentGoalList', {segmentId, limit: 10, offset: 0});

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.addUpdateGoal', error);

      return Promise.reject(error);
    }
  },
  async copySegment({rootState}, payload) {
    try {
      const {riaId} = rootState;

      const {cloneSegmentId, segmentRequest} = payload;
      const sdkRequest = {riaId, cloneSegmentId, payload: segmentRequest};
      const fullPayload = addRiaComponentTo(sdkRequest, rootState.settings.default);
      const sdkResponse = await RwAdvisorService.createCloneSegment(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('segmentSettings.copySegment', error);

      return Promise.reject(error);
    }
  },
};

export const mutations = {
  SET_SEGMENT_SETTINGS(state, segments) {
    state.segments = segments;
  },

  SET_CURRENT_SEGMENT(state, segment) {
    state.currentSegment = Object.assign(new SegmentModel(), {...segment});
  },

  SET_GOAL_LIST(state, goals) {
    state.currentSegment.onboarding.goals = goals.map((goal) =>
      GoalModel.make({
        ...goal,
        isEditable: false,
      })
    );
  },

  SET_ACCOUNT_OPENING(state, accountOpening) {
    const objAccountOpening = AccountOpeningModel.make(accountOpening);

    state.currentSegment.onboarding.accountOpening = {...objAccountOpening};
  },

  SET_GOAL_THUMBNAILS(state, goalThumbnailList) {
    state.goalThumbnailList = goalThumbnailList;
  },

  SET_CURRENT_SEGMENT_FEATURES(state, featureList) {
    state.currentSegment.featureList = featureList;
  },

  UPDATE_FEATURE_STATUS(state, {featureConstant, featureStatus}) {
    state.currentSegment.featureList = state.currentSegment.featureList.map((objFeature) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const _objFeature = objFeature;

      if (_objFeature.featureConstant === featureConstant) {
        _objFeature.featureStatus = featureStatus;
      }

      return _objFeature;
    });
  },

  UPDATE_SEGMENT_NAME(state, {segmentLabel}) {
    state.currentSegment.segmentLabel = segmentLabel;
  },
};

export default {
  namespaced: true,
  state: segmentSettingState,
  getters,
  actions,
  mutations,
};
